import { get } from "lodash";
import { FormEvent, useMemo, useState } from "react";
import { generateUUID } from "utills/helpers";
import Button from "components/button";
import { Modal } from "components/modal";
import Pagination from "components/pagination";

interface PagingType {
  total_item: number;
  total_page: number;
  current_page: number;
}

interface PropType {
  open: boolean;
  data: Record<string, string>[];
  loading: boolean;
  handleClose: () => void;
  handleSubmitImportAttendant: (
    data: Record<string, string>[]
  ) => Promise<void>;
}

const ValidImportAttendantModal = (props: PropType) => {
  const { open, data, loading, handleClose, handleSubmitImportAttendant } =
    props;

  const PAGE_SIZE = 5;
  const [page, setPage] = useState<number>(1);

  // Headers dynamic từ dòng đầu tiên
  const HEADERS = useMemo(() => {
    if (!data || data.length === 0) return [];
    return Object.keys(data[0]);
  }, [data]);

  // Paginated data
  const PAGINATED_DATA = useMemo(() => {
    if (!data) return [];

    const start = (page - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;

    return data.slice(start, end);
  }, [data, page]);

  const invalidPaging: PagingType = useMemo(() => {
    return {
      total_item: data?.length || 0,
      total_page: Math.ceil((data?.length || 0) / PAGE_SIZE),
      current_page: page,
    };
  }, [data, page]);

  const handlePageChange = (value: number) => {
    setPage(value);
  };

  const handleCloseModal = () => {
    handleClose();
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmitImportAttendant(data);
  };

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      title="Import Attendants"
      closeOnClickBackdrop={false}
    >
      <form className="p-6 flex flex-col gap-8" onSubmit={handleSubmit}>
        <div className="max-w-full md:max-w-[80vw] flex flex-col gap-5">
          <div className="flow-root">
            <div className="overflow-x-auto min-w-full overflow-y-auto">
              <table className="mb-6 w-full divide-y divide-gray-300 bg-magnolia-500 rounded-md">
                <thead>
                  <tr>
                    <th className="th py-3.5 pl-4 pr-3 sm:pl-6 lg:pl-8">No</th>

                    {HEADERS.map((key) => (
                      <th key={key} className="th min-w-[200px]">
                        {key.replaceAll("_", " ").toUpperCase()}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 bg-white">
                  {PAGINATED_DATA.map((item, index) => (
                    <tr key={generateUUID()}>
                      <td className="td py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                        {(invalidPaging.current_page - 1) * PAGE_SIZE +
                          index +
                          1}
                      </td>

                      {HEADERS.map((key) => (
                        <td key={key} className="td min-w-[200px]">
                          <span>{get(item, key, "")}</span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination
            currentPage={invalidPaging.current_page}
            totalPage={invalidPaging.total_page}
            total={{
              totalItems: PAGINATED_DATA.length,
              total: invalidPaging.total_item,
            }}
            onChange={handlePageChange}
          />
        </div>

        <div className="flex items-center justify-end gap-x-3">
          <Button
            className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
            onClick={handleCloseModal}
            type="button"
          >
            Cancel
          </Button>

          <Button type="submit" className="w-20" loading={loading}>
            Import
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ValidImportAttendantModal;
